import { useState, useEffect } from 'react';
import { useSafeAppsSDK } from '@safe-global/safe-apps-react-sdk';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Alert,
  CircularProgress,
  Chip,
  Stack,
  Tabs,
  Tab,
} from '@mui/material';
import { Shield, Lock, Key, Security, Build, Group, Science } from '@mui/icons-material';

import { SafeAppState } from '@/types/safe';
import { SecurityLevel } from '@/types/quantum';
import { NISTQuantumSafe } from '@/crypto/NISTQuantumSafe';
import { QuantumKeyManager } from '@/crypto/KeyManager';
import SafeIntegratedAssetManager from '@/components/SafeIntegratedAssetManager';
import VaultOperations from '@/components/VaultOperations';
import { ContractTest } from '@/components/ContractTest';

// Import reset utilities for browser console access
import '@/utils/resetStorage';

function App() {
  const { safe, sdk } = useSafeAppsSDK();
  const [appState, setAppState] = useState<SafeAppState>({
    safe: safe,
    isLoading: true,
    error: null,
    assets: [],
    selectedAssets: [],
    isConnected: false,
  });

  const [securityLevel] = useState<SecurityLevel>(5); // Default to Category 5 (AES-256 equivalent)
  const [activeTab, setActiveTab] = useState(0);

  // Initialize quantum cryptography instances
  const [quantumSafe] = useState(() => new NISTQuantumSafe(securityLevel));
  const [keyManager] = useState(() => new QuantumKeyManager(securityLevel));


  useEffect(() => {
    const initializeApp = async () => {
      try {
        setAppState(prev => ({ ...prev, isLoading: true, error: null }));

        console.log('Initializing Safe App...');
        console.log('Safe object:', safe);
        console.log('SDK object:', sdk);

        // Use the SDK instance from the hook
        console.log('Using SDK instance:', sdk);

        // Check if we're in a Safe environment (iframe)
        const isInIframe = window.self !== window.top;
        console.log('Running in iframe:', isInIframe);

        if (isInIframe) {
          // We're in Safe Wallet - initialize proper SDK communication
          try {
            // Send initialization message to parent (Safe Wallet)
            if (window.parent) {
              window.parent.postMessage(
                { messageId: 'SAFE_APP_SDK_INITIALIZED', version: '1.0.0' },
                '*'
              );
            }

            console.log('Attempting to get Safe info via SDK...');
            const safeInfo = await sdk.safe.getInfo();
            console.log('Safe Info retrieved:', safeInfo);

            // The Safe SDK handles all wallet connections internally
            // No need for external wallet connection in Safe iframe context
            console.log('Safe SDK will handle all wallet operations');

            setAppState(prev => ({
              ...prev,
              safe: safeInfo,
              isConnected: true,
              isLoading: false,
            }));

          } catch (sdkError) {
            console.error('Safe SDK initialization failed:', sdkError);

            // Fallback to provided safe object if available
            if (safe && safe.safeAddress) {
              console.log('Falling back to provided safe object');
              setAppState(prev => ({
                ...prev,
                safe: safe,
                isConnected: true,
                isLoading: false,
              }));
            } else {
              throw new Error('Safe SDK initialization failed and no Safe data available');
            }
          }

        } else {
          // Demo mode for standalone testing
          console.log('Running in demo mode (standalone)');
          const demoSafe = {
            safeAddress: '0x1234567890123456789012345678901234567890',
            chainId: 11155111, // Sepolia testnet
            isReadOnly: false,
            owners: ['0xDemo1234567890123456789012345678901234567890'],
            threshold: 1,
          };

          setAppState(prev => ({
            ...prev,
            safe: demoSafe as any,
            isConnected: false, // Mark as demo
            isLoading: false,
          }));
        }

      } catch (error) {
        console.error('Failed to initialize app:', error);
        setAppState(prev => ({
          ...prev,
          error: error instanceof Error ? error.message : 'Failed to initialize app',
          isLoading: false,
          isConnected: false,
        }));
      }
    };

    // Always try to initialize
    initializeApp();
  }, [safe, sdk]);

  const getSecurityLevelInfo = (level: SecurityLevel) => {
    switch (level) {
      case 3:
        return {
          name: 'Category 3',
          description: 'AES-192 equivalent quantum resistance',
          color: 'warning' as const,
          algorithms: ['ML-KEM-768', 'ML-DSA-65', 'SLH-DSA-SHA2-192f'],
        };
      case 5:
        return {
          name: 'Category 5',
          description: 'AES-256 equivalent quantum resistance',
          color: 'success' as const,
          algorithms: ['ML-KEM-1024', 'ML-DSA-87', 'SLH-DSA-SHA2-256f'],
        };
      default:
        return {
          name: 'Unknown',
          description: 'Unknown security level',
          color: 'error' as const,
          algorithms: [],
        };
    }
  };

  const securityInfo = getSecurityLevelInfo(securityLevel);

  if (appState.isLoading) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          minHeight="400px"
          gap={2}
        >
          <CircularProgress size={48} className="quantum-glow" />
          <Typography variant="h6" color="text.secondary">
            Initializing Quantum Safe Vault...
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Establishing secure connection to Safe wallet
          </Typography>
        </Box>
      </Container>
    );
  }

  if (appState.error) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="error" sx={{ mb: 3 }}>
          <Typography variant="h6" component="div" gutterBottom>
            Connection Error
          </Typography>
          {appState.error}
        </Alert>

        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              How to use Quantum Safe Vault
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              1. Open your Safe wallet at{' '}
              <a href="https://app.safe.global" target="_blank" rel="noopener noreferrer">
                app.safe.global
              </a>
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              2. Navigate to Apps → Custom App
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              3. Enter this app's URL: <code>http://localhost:3000</code>
            </Typography>
            <Typography variant="body2" color="text.secondary">
              4. The Quantum Safe Vault will load with access to your Safe assets
            </Typography>
          </CardContent>
        </Card>
      </Container>
    );
  }

  return (
    <div className="safe-app-container">
      <Container maxWidth="lg" sx={{ py: 3 }}>
        {/* Header */}
        <Box mb={4}>
          <Stack direction="row" alignItems="center" spacing={2} mb={2}>
            <Shield sx={{ fontSize: 32, color: 'primary.main' }} />
            <Typography variant="h4" component="h1" fontWeight="600">
              Quantum Safe Vault
            </Typography>
            <Chip
              label={securityInfo.name}
              color={securityInfo.color}
              variant="outlined"
              size="small"
              className={`security-level-${securityLevel}`}
            />
          </Stack>

          <Typography variant="body1" color="text.secondary" paragraph>
            NIST post-quantum cryptography protection for your Safe wallet assets against future quantum computer attacks.
          </Typography>
        </Box>

        {/* Safe Connection Status */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            {!appState.isConnected && (
              <Stack direction="row" alignItems="center" spacing={2} mb={2}>
                <Lock sx={{ color: 'warning.main' }} />
                <Typography variant="h6">
                  Demo Mode - Safe Disconnected
                </Typography>
                <Chip
                  label="DEMO"
                  color="warning"
                  size="small"
                  variant="outlined"
                />
              </Stack>
            )}

            {!appState.isConnected && (
              <Alert severity="info" sx={{ mb: 2 }}>
                Running in demo mode. To use with real Safe assets:
                <br />
                1. Go to <strong>app.safe.global</strong>
                <br />
                2. Navigate to <strong>Apps → Custom App</strong>
                <br />
                3. Enter: <strong>http://localhost:3001/</strong>
              </Alert>
            )}

            <Box display="flex" flexDirection="column" gap={3}>
              <Box
                sx={{
                  backgroundColor: 'primary.50',
                  border: '1px solid',
                  borderColor: 'primary.200',
                  borderRadius: 2,
                  p: 2
                }}
              >
                <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
                  {/* Threshold/Owners on the left */}
                  <Box sx={{ minWidth: 'fit-content' }}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Threshold/Owners
                    </Typography>
                    <Typography variant="body2" fontWeight="medium">
                      {appState.safe.threshold}/{appState.safe.owners.length}
                    </Typography>
                  </Box>

                  {/* Connected to Safe Address in the center */}
                  <Box sx={{ flex: 1, textAlign: 'center' }}>
                    <Stack direction="row" alignItems="center" justifyContent="center" spacing={1} sx={{ mb: 1 }}>
                      <Lock sx={{ color: 'success.main' }} />
                      <Typography variant="h6" color="primary.main">
                        Connected to Safe Address:
                      </Typography>
                    </Stack>
                    <Typography
                      variant="body1"
                      fontFamily="monospace"
                      sx={{
                        fontSize: '0.9rem',
                        letterSpacing: '0.02em',
                        wordBreak: 'break-all',
                        color: 'text.primary'
                      }}
                    >
                      {appState.safe.safeAddress}
                    </Typography>
                  </Box>

                  {/* Network on the right */}
                  <Box sx={{ minWidth: 'fit-content', textAlign: 'right' }}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Network
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 500,
                        color: 'text.primary'
                      }}
                    >
                      {appState.safe.chainId === 1 ? 'Ethereum Mainnet' :
                       appState.safe.chainId === 137 ? 'Polygon' :
                       appState.safe.chainId === 100 ? 'Gnosis Chain' :
                       appState.safe.chainId === 56 ? 'BSC' :
                       appState.safe.chainId === 42161 ? 'Arbitrum One' :
                       appState.safe.chainId === 10 ? 'Optimism' :
                       appState.safe.chainId === 8453 ? 'Base' :
                       `Chain ID: ${appState.safe.chainId}`}
                    </Typography>
                  </Box>
                </Stack>
              </Box>
            </Box>
          </CardContent>
        </Card>

        {/* Security Configuration */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Stack direction="row" alignItems="center" spacing={2} mb={2}>
              <Key sx={{ color: 'primary.main' }} />
              <Typography variant="h6">
                Quantum Security Configuration
              </Typography>
            </Stack>

            <Typography variant="body2" color="text.secondary" paragraph>
              {securityInfo.description}
            </Typography>

            <Box>
              <Typography variant="subtitle2" gutterBottom>
                NIST Post-Quantum Algorithms:
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {securityInfo.algorithms.map((algorithm) => (
                  <Chip
                    key={algorithm}
                    label={algorithm}
                    size="small"
                    variant="outlined"
                  />
                ))}
              </Stack>
            </Box>
          </CardContent>
        </Card>

        {/* Main Application Tabs */}
        <Box sx={{ width: '100%', mb: 3 }}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs value={activeTab} onChange={(_, newValue) => setActiveTab(newValue)}>
              <Tab
                icon={<Group />}
                iconPosition="start"
                label="Safe Asset Manager"
                sx={{ minHeight: 48, textTransform: 'none' }}
              />
              <Tab
                icon={<Security />}
                iconPosition="start"
                label="Vault Operations"
                sx={{ minHeight: 48, textTransform: 'none' }}
              />
              <Tab
                icon={<Science />}
                iconPosition="start"
                label="Contract Test"
                sx={{ minHeight: 48, textTransform: 'none' }}
              />
              <Tab
                icon={<Build />}
                iconPosition="start"
                label="Settings"
                sx={{ minHeight: 48, textTransform: 'none' }}
              />
            </Tabs>
          </Box>

          {/* Tab Content */}
          <Box sx={{ pt: 3 }}>
            {activeTab === 0 && (
              <SafeIntegratedAssetManager
                keyManager={keyManager}
                quantumSafe={quantumSafe}
                isConnected={appState.isConnected}
              />
            )}

            {activeTab === 1 && (
              <VaultOperations
                keyManager={keyManager}
                quantumSafe={quantumSafe}
                safeAddress={appState.safe.safeAddress}
                isConnected={appState.isConnected}
              />
            )}

            {activeTab === 2 && (
              <ContractTest />
            )}

            {activeTab === 3 && (
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    <Build sx={{ mr: 1, verticalAlign: 'middle' }} />
                    Application Settings
                  </Typography>

                  <Alert severity="info" sx={{ mb: 2 }}>
                    <Typography variant="h6" component="div" gutterBottom>
                      Phase 2: NIST Post-Quantum Cryptography ✅
                    </Typography>
                    <Typography variant="body2" paragraph>
                      Complete NIST post-quantum implementation now active:
                    </Typography>
                    <ul style={{ margin: 0, paddingLeft: '20px' }}>
                      <li>✅ Real NIST ML-KEM, ML-DSA, and SLH-DSA implementations</li>
                      <li>✅ Quantum-safe key derivation and management system</li>
                      <li>✅ Asset encryption/decryption interface components</li>
                      <li>✅ Real-time encryption/decryption operations</li>
                    </ul>
                  </Alert>

                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle1" gutterBottom>
                      Current Configuration
                    </Typography>
                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 2 }}>
                      <Box>
                        <Typography variant="body2" color="text.secondary">Security Level</Typography>
                        <Chip
                          label={`Category ${securityLevel}`}
                          color={securityLevel === 5 ? 'success' : 'warning'}
                          size="small"
                        />
                      </Box>
                      <Box>
                        <Typography variant="body2" color="text.secondary">Key Management</Typography>
                        <Typography variant="body2">Hierarchical Deterministic (HD)</Typography>
                      </Box>
                      <Box>
                        <Typography variant="body2" color="text.secondary">Encryption</Typography>
                        <Typography variant="body2">Hybrid (PQ + AES-256-GCM)</Typography>
                      </Box>
                    </Box>
                  </Box>

                  <Box sx={{ mt: 3 }}>
                    <Typography variant="subtitle1" gutterBottom>
                      Next Development Phase
                    </Typography>
                    <Typography variant="body2" color="text.secondary" paragraph>
                      Phase 3 will focus on smart contract deployment and blockchain integration:
                    </Typography>
                    <ul style={{ margin: 0, paddingLeft: '20px', color: 'text.secondary' }}>
                      <li>QuantumSafeVault.sol contract development</li>
                      <li>Multi-network deployment and verification</li>
                      <li>Safe transaction integration</li>
                      <li>Gas optimization and batch operations</li>
                    </ul>
                  </Box>
                </CardContent>
              </Card>
            )}
          </Box>
        </Box>

      </Container>
    </div>
  );
}

export default App;